import { translate } from 'preact-i18n';
import translations from '../translations/i18n';
/**
 * Receives a passengers object with the type as the key and the quantity
 * as the value and returns a string with the passengers encoded for
 * query params or for the URL
 * ```
 * if type is queryParams:
 *    { adult: 2, child: 1, infant: 1 } => adult=2&child=1&infant=1
 * else:
 *    { adult: 2, child: 1, infant: 1 } => A2-C1-I1
 * ```
 * @method encodePassengers
 * @param {Object} passengers Passengers object
 * @return {String}
 */
export default function encodePassengers(passengers) {
  const passengerTypes = Object.keys(passengers);
  const encoded = [];

  passengerTypes.forEach(passengerType => {
    const quantity = passengers[passengerType];

    if (quantity > 0) {
      encoded.push(`${passengerType.substr(0, 1).toUpperCase()}${quantity}`);
    }
  });

  return encoded.join('-');
}

export const encodeCustomPassengers = passengers => {
  const passengerTypes = Object.keys(passengers);
  const encoded = [];

  passengerTypes.forEach(passengerType => {
    const quantity = passengers[passengerType];

    if (quantity > 0) {
      encoded.push(`${passengerType}-${quantity}`);
    }
  });
  return encoded.join(',');
};

/** Empresa Unida Overflow gratuities + lap child (no minor): copy and rows match design QA */
export const getPassengersSelectorVariant = (brand, passengersOptions) => {
  if (!passengersOptions || typeof passengersOptions !== 'string') return 'default';
  const tokens = passengersOptions.split(',').map(p => p.trim()).filter(Boolean);
  if (brand !== 'empresaunida') return 'default';
  if (!tokens.includes('child_in_arms')) return 'default';
  if (tokens.includes('minor')) return 'default';

  return 'unida-overflow';
};

export const getPassengersTypes = (lang, { variant = 'default', brand, labels = {} } = {}) => {
  const dictionary = translations[lang];
  const isUnidaOverflow = variant === 'unida-overflow';
  const isEmpresaUnida = brand === 'empresaunida';
  const defaultGeneralDescription = isUnidaOverflow
    ? ''
    : translate('adult_description', 'text', dictionary, { years_old: 12 });

  /**
   * Resolution chain for each gratuity-row label/description:
   * - tenant prop override (labels.*)  →  raw string from data-prop-*
   * - empresaunida brand-gated default →  i18n key shipped with the widget
   *   (only when the key exists in the active locale dictionary)
   * - generic default                  →  i18n key for any other brand
   */
  const hasOverride = value => value !== undefined && value !== null;
  const dictHas = (section, key) =>
    Boolean(dictionary && dictionary[section] && dictionary[section][key]);
  const resolveCopy = ({ override, section, empresaUnidaKey, genericValue, params }) => {
    if (hasOverride(override)) return override;
    if (isEmpresaUnida && empresaUnidaKey && dictHas(section, empresaUnidaKey)) {
      return translate(empresaUnidaKey, section, dictionary, params);
    }
    return genericValue;
  };

  const generalTitle = resolveCopy({
    override: labels.generalTitle,
    section: 'label',
    empresaUnidaKey: 'passenger_normal',
    genericValue: translate('adult', 'label', dictionary),
  });
  const generalDescription = resolveCopy({
    override: labels.generalDescription,
    section: 'text',
    empresaUnidaKey: 'passenger_normal_description',
    genericValue: defaultGeneralDescription,
  });
  const childInArmsTitle = resolveCopy({
    override: labels.childInArmsTitle,
    section: 'label',
    empresaUnidaKey: null,
    genericValue: translate('child_in_arms', 'label', dictionary),
  });
  const childInArmsDescription = resolveCopy({
    override: labels.childInArmsDescription,
    section: 'text',
    empresaUnidaKey: 'passenger_baby_in_arms_age',
    genericValue: translate('child_in_arms_description', 'text', dictionary),
  });
  const olderTitle = resolveCopy({
    override: labels.olderTitle,
    section: 'label',
    empresaUnidaKey: 'passenger_elderly',
    genericValue: translate('insen', 'label', dictionary),
  });
  const olderDescription = resolveCopy({
    override: labels.olderDescription,
    section: 'text',
    empresaUnidaKey: 'passenger_elderly_eligibility',
    genericValue: translate('required_credential', 'text', dictionary),
  });
  const disabilityTitle = resolveCopy({
    override: labels.disabilityTitle,
    section: 'label',
    empresaUnidaKey: 'passenger_free_pass',
    genericValue: translate('passenger_disability', 'label', dictionary),
  });
  const disabilityDescription = resolveCopy({
    override: labels.disabilityDescription,
    section: 'text',
    empresaUnidaKey: 'passenger_free_pass_holder',
    genericValue: '',
  });
  const disadvantagedYouthTitle = resolveCopy({
    override: labels.disadvantagedYouthTitle,
    section: 'label',
    empresaUnidaKey: null,
    genericValue: translate('passenger_low_income_youth', 'label', dictionary),
  });
  const disadvantagedYouthDescription = resolveCopy({
    override: labels.disadvantagedYouthDescription,
    section: 'text',
    empresaUnidaKey: null,
    genericValue: translate('passenger_id_jovem_required', 'text', dictionary),
  });

  return {
    adult: {
      name: 'adult',
      defaultValue: 1,
      title: translate('adult', 'label', dictionary),
      description: translate('adult_description', 'text', dictionary, { years_old: 12 }),
      minValue: 0,
      isEnabled: true,
      isIndependent: true,
    },
    general: {
      name: 'general',
      defaultValue: 1,
      title: generalTitle,
      description: generalDescription,
      minValue: 0,
      isEnabled: true,
      isIndependent: true,
    },
    minor: {
      name: 'minor',
      defaultValue: 0,
      title: translate('minor', 'label', dictionary),
      description: translate('minor_description', 'text', dictionary, {
        younger_age: 3,
        older_age: 11,
      }),
      minValue: 0,
      isEnabled: true,
    },
    older: {
      name: 'older',
      defaultValue: 0,
      title: olderTitle,
      description: olderDescription,
      minValue: 0,
      isEnabled: true,
      isIndependent: true,
    },
    disability: {
      name: 'disability',
      defaultValue: 0,
      title: disabilityTitle,
      description: disabilityDescription,
      minValue: 0,
      isEnabled: true,
      isIndependent: true,
    },
    disadvantaged_youth: {
      name: 'disadvantaged_youth',
      defaultValue: 0,
      title: disadvantagedYouthTitle,
      description: disadvantagedYouthDescription,
      minValue: 0,
      isEnabled: true,
      isIndependent: true,
    },
    pet_friendly: {
      name: 'pet_friendly',
      defaultValue: 0,
      title: translate('pet', 'label', dictionary),
      description: translate('pet', 'text', dictionary),
      minValue: 0,
      isEnabled: true,
    },
    infant: {
      name: 'infant',
      defaultValue: 0,
      title: translate('infant', 'label', dictionary),
      description: translate('infant_description', 'text', dictionary, { older_age: 2 }),
      minValue: 0,
      isEnabled: true,
    },
    child_in_arms: {
      name: 'child_in_arms',
      defaultValue: 0,
      title: childInArmsTitle,
      description: childInArmsDescription,
      minValue: 0,
      isEnabled: true,
      infoModalKey: 'child_in_arms_info',
    },
    special: {
      name: 'special',
      defaultValue: 0,
      title: translate('pcd', 'label', dictionary),
      description: translate('pcd', 'text', dictionary),
      minValue: 0,
      isEnabled: true,
      isIndependent: true,
    },
    wheelchair_handicap: {
      name: 'wheelchair_handicap',
      defaultValue: 0,
      title: translate('pcd', 'label', dictionary),
      description: translate('pcd', 'text', dictionary),
      minValue: 0,
      isEnabled: true,
      isIndependent: true,
    },
    teacher: {
      name: 'teacher',
      defaultValue: 0,
      title: translate('professor', 'label', dictionary),
      description: translate('required_credential', 'text', dictionary),
      minValue: 0,
      isEnabled: false,
      isIndependent: true,
    },
    student: {
      name: 'student',
      defaultValue: 0,
      title: translate('student', 'label', dictionary),
      description: translate('required_credential', 'text', dictionary),
      minValue: 0,
      isEnabled: false,
      isIndependent: true,
    },
  };
};

export const getPassengersSelectorList = ({ passengers, lang, variant = 'default', brand, labels = {} }) => {
  if (!passengers) {
    return [];
  }
  const types = getPassengersTypes(lang, { variant, brand, labels });
  return passengers.reduce((acc, passenger) => {
    const validPassengers = types[passenger];
    if (validPassengers) {
      acc.push(validPassengers);
    }
    return acc;
  }, []);
};

export const getSeasonPassengersSelectorList = ({ passengers, lang, variant = 'default', brand, labels = {} }) => {
  if (!passengers) {
    return [];
  }
  const types = getPassengersTypes(lang, { variant, brand, labels });
  return passengers.reduce((acc, passenger) => {
    const validPassengers = types[passenger];
    if (validPassengers) {
      validPassengers.isEnabled = false;
      acc.push(validPassengers);
    }
    return acc;
  }, []);
};
